// Collection landing pages — one canonical entry per URL. Kept in a single
// data model so /collections/$slug can render every page from the same
// component while each URL still ships genuinely distinct copy, intent and
// internal links (not thin doorway pages).
//
// IMPORTANT: never invent products, prices, certificates, ratings or
// outcomes. Any product referenced here must exist in src/lib/clusters.ts /
// the PDP catalogue. Wording uses "supports"/"may support" — no diagnose,
// treat, cure or drug-comparison language.

export type CollectionProductRef = {
  handle: "mag-absorb-pro" | "liver-biome-plus" | "ova-balance";
  title: string;
  tagline: string;
  bullets: string[];
};

export type CollectionExternalRef = {
  // Non-PDP destination (e.g. /kids or /shop). We render an ItemList entry
  // and a CTA card, but never Product/Offer schema for these.
  to: "/kids" | "/shop" | "/ingredients" | "/quiz";
  title: string;
  tagline: string;
  bullets: string[];
};

export type CollectionArticleRef = {
  slug: string;
  title: string;
  excerpt: string;
};

export type CollectionIngredientRef = {
  anchor: string; // matches an id on /ingredients
  label: string;
  note: string;
};

export type CollectionFaq = { q: string; a: string };

export type CollectionPage = {
  slug:
    | "magnesium-supplements"
    | "sleep-recovery"
    | "inositol-supplements"
    | "pcos-nutrition"
    | "liver-gut-support"
    | "kids-nutrition";
  seoTitle: string; // 45-60 chars target
  seoDescription: string; // 130-160 chars target
  h1: string;
  eyebrow: string;
  intro: string; // direct-answer opener, 1-2 sentences
  overview: string; // longer body paragraph rendered server-side
  selectionGuide: {
    heading: string;
    intro: string;
    items: { title: string; body: string }[];
  };
  products: CollectionProductRef[]; // real SKUs (may be empty for kids page)
  external?: CollectionExternalRef; // secondary CTA route
  ingredients: CollectionIngredientRef[];
  safety: string[]; // clinician / population guidance
  articles: CollectionArticleRef[]; // internal blog links
  faqs: CollectionFaq[];
  /** Ova ecosystem bundle slugs surfaced as "build your protocol" cards. */
  bundleSlugs?: string[];
  breadcrumbLabel: string;
};

const PRODUCT_LIB: Record<CollectionProductRef["handle"], CollectionProductRef> = {
  "mag-absorb-pro": {
    handle: "mag-absorb-pro",
    title: "Mag Absorb Pro",
    tagline: "5-form magnesium · sleep, calm, muscle recovery",
    bullets: [
      "Liposomal Magnesium Bisglycinate + Aquamin® marine magnesium",
      "Chloride, Aspartate and Gluconate for full-spectrum coverage",
      "Zinc, Copper, P-5-P and Vitamin D3 cofactors in one tablet",
    ],
  },
  "liver-biome-plus": {
    handle: "liver-biome-plus",
    title: "Liver Biome Plus",
    tagline: "NAC · liposomal silymarin · curcumin · ALA + probiotic",
    bullets: [
      "NAC 600 mg with liposomal Silymarin (350 mg eq)",
      "Liposomal Curcumin and Alpha-Lipoic Acid",
      "10B CFU multi-strain probiotic for the gut-liver axis",
    ],
  },
  "ova-balance": {
    handle: "ova-balance",
    title: "Ova Balance",
    tagline: "40:1 Myo:D-Chiro Inositol · PCOS & cycle support",
    bullets: [
      "Gold-standard 40:1 Myo + D-Chiro Inositol (2050 mg)",
      "Berberine HCl 300 mg with Quatrefolic® L-5-MTHF",
      "Magnesium, Zinc, Chromium and Vitamin D3 cofactors",
    ],
  },
};

export const COLLECTIONS: Record<CollectionPage["slug"], CollectionPage> = {
  "magnesium-supplements": {
    slug: "magnesium-supplements",
    seoTitle: "Magnesium Glycinate & Sleep Supplements India | TruWe",
    seoDescription:
      "Magnesium glycinate (bisglycinate) for sleep, calm and recovery. Compare magnesium forms, doses and timing, and see what to look for on an Indian label.",
    h1: "Magnesium supplements, chosen by the form your body actually absorbs",
    eyebrow: "Magnesium collection",
    intro:
      "Not all magnesium is the same. The form on the label decides how much your body actually absorbs and where it acts, sleep, muscle, mood or digestion. For sleep, magnesium glycinate (also called bisglycinate) is the form most people look for.",
    overview:
      "Most Indian magnesium tablets use magnesium oxide, an inexpensive form with limited oral absorption. TruWe uses five better-studied forms in one tablet, liposomal magnesium bisglycinate, Aquamin® marine magnesium, magnesium chloride, aspartate and gluconate, to cover the spectrum of magnesium activity in the body and reduce the trade-offs of any single salt. Mag Absorb Pro is taken as one tablet before bed and contains no melatonin.",
    selectionGuide: {
      heading: "How to choose your magnesium",
      intro:
        "Pick by the outcome you care about, the underlying form matters more than the milligrams on the tin.",
      items: [
        {
          title: "For sleep, stress and calm",
          body: "Look for magnesium bisglycinate. Glycine is a calming amino acid and this chelated form is well tolerated on an empty stomach.",
        },
        {
          title: "For muscle cramps and recovery",
          body: "Blends that include Aquamin® marine magnesium and magnesium chloride are useful, chloride is easily ionised and marine magnesium delivers naturally occurring trace minerals.",
        },
        {
          title: "For general daily support",
          body: "A multi-form blend covers gaps that a single salt cannot. Combining bisglycinate with aspartate and gluconate supports both nervous-system and cellular-energy pathways.",
        },
        {
          title: "Forms we deliberately avoid",
          body: "Magnesium oxide (low bioavailability) and high-dose magnesium citrate on its own (loose stools at active doses). Read the label.",
        },
      ],
    },
    products: [PRODUCT_LIB["mag-absorb-pro"]],
    ingredients: [
      { anchor: "magnesium-bisglycinate", label: "Magnesium Bisglycinate", note: "Chelated, well tolerated, calming glycine carrier." },
      { anchor: "aquamin", label: "Aquamin® Marine Magnesium", note: "Seaweed-derived multi-mineral matrix." },
    ],
    safety: [
      "If you take blood-pressure medication, diuretics or bisphosphonates, speak to your doctor before starting a magnesium supplement.",
      "Pregnant or nursing? Please check with your clinician, daily magnesium intake targets differ.",
      "This is a food supplement, not a substitute for a varied diet or medical care.",
    ],
    articles: [
      { slug: "magnesium-benefits", title: "Magnesium Benefits: Forms, Foods & Dosage Guide", excerpt: "Which magnesium form does what, and what to look for on a label." },
      { slug: "best-magnesium-supplement-for-sleep", title: "Best Magnesium Supplement for Sleep: A Complete Guide", excerpt: "How magnesium may support sleep, and how to pick a well-formulated one." },
    ],
    faqs: [
      { q: "Is magnesium glycinate the same as magnesium bisglycinate?", a: "Yes. Both names describe magnesium bound to the amino acid glycine. 'Bisglycinate' is the more precise term because each magnesium is bound to two glycine molecules." },
      { q: "Is magnesium glycinate good for sleep?", a: "Magnesium supports normal nervous-system and muscle function, and glycine has been studied for its role in sleep. Magnesium glycinate is a popular evening choice because it is gentle on the stomach. It supports a good sleep routine rather than acting like a sleeping pill." },
      { q: "Which magnesium form is most bioavailable?", a: "Chelated forms like magnesium bisglycinate are generally better absorbed than magnesium oxide. Marine magnesium (Aquamin®) is also well-studied for absorption." },
      { q: "When should I take magnesium for sleep?", a: "Most people take it 30 to 60 minutes before bed. Mag Absorb Pro is one tablet before bed." },
      { q: "Magnesium glycinate vs citrate: which is better?", a: "Glycinate is usually preferred for evening use and sensitive stomachs. Citrate is often chosen for occasional constipation because it draws water into the gut, which can cause loose stools at higher doses." },
      { q: "Is a single magnesium form enough?", a: "A single form can work if it matches your goal. Multi-form blends aim to reduce the trade-offs of any one salt." },
      { q: "Can I take magnesium every day?", a: "For most healthy adults, daily magnesium within label directions is fine. Check with your doctor if you have kidney problems or take blood-pressure medicine, diuretics, antibiotics or bisphosphonates." },
    ],
    breadcrumbLabel: "Magnesium supplements",
  },

  "sleep-recovery": {
    slug: "sleep-recovery",
    seoTitle: "Sleep & Recovery Supplements. Magnesium, Glycine | TruWe",
    seoDescription:
      "Evidence-first sleep and recovery support. See how magnesium bisglycinate, Aquamin® and glycine may support deeper rest and next-day recovery.",
    h1: "Sleep and recovery support, built on the calming side of magnesium",
    eyebrow: "Sleep & recovery",
    intro:
      "Better sleep rarely comes from a single hero ingredient. Magnesium status, mineral balance and evening ritual work together, and small, sustained inputs beat one loud one.",
    overview:
      "Poor sleep is often linked to sub-optimal magnesium status and unwound nervous-system tone at bedtime. Magnesium bisglycinate pairs magnesium with glycine, an amino acid studied for its role in sleep onset. TruWe's evening protocol is designed around this combination rather than melatonin or sedating herbs.",
    selectionGuide: {
      heading: "How to build an evening routine that lasts",
      intro:
        "Consistency compounds. These are the levers we prioritise before reaching for stronger interventions.",
      items: [
        { title: "Anchor your magnesium in the evening", body: "A chelated form like bisglycinate taken 30-60 minutes before bed supports the wind-down window without daytime drowsiness." },
        { title: "Protect the last hour", body: "Dim overhead lights, cool the room and stop caffeine at least eight hours before your intended bedtime." },
        { title: "Recovery is a 24-hour signal", body: "Daytime hydration, mineral status and morning light exposure quietly decide how the evening lands." },
        { title: "When to escalate", body: "If sleep issues persist beyond a few weeks despite good sleep hygiene, please speak to a clinician rather than layering supplements." },
      ],
    },
    products: [PRODUCT_LIB["mag-absorb-pro"]],
    ingredients: [
      { anchor: "magnesium-bisglycinate", label: "Magnesium Bisglycinate", note: "Chelated magnesium with glycine, studied for its calming profile." },
      { anchor: "aquamin", label: "Aquamin® Marine Magnesium", note: "Multi-mineral matrix that complements the bisglycinate." },
    ],
    safety: [
      "Do not combine sleep supplements with alcohol or sedative medication without medical advice.",
      "If you are on antidepressants, blood pressure or sleep medication, check with your doctor before starting.",
      "Not intended for children, see the TruWe Kids range for age-appropriate options.",
    ],
    articles: [
      { slug: "best-magnesium-supplement-for-sleep", title: "Best Magnesium Supplement for Sleep: A Complete Guide", excerpt: "Timing, dose and form, how to give magnesium a fair shot at helping sleep." },
      { slug: "magnesium-benefits", title: "Magnesium Benefits: Forms, Foods & Dosage Guide", excerpt: "The forms of magnesium, ranked by what they are actually good for." },
    ],
    faqs: [
      { q: "Will magnesium make me sleepy during the day?", a: "Well-tolerated forms like bisglycinate are not sedative. Most people take them in the evening simply because that is when the effect is most useful." },
      { q: "Is this the same as melatonin?", a: "No. Mineral status and melatonin do different jobs. TruWe's protocol does not contain melatonin." },
      { q: "How long before I notice a difference?", a: "Give any daily supplement 2-4 weeks of consistent use before judging its effect." },
    ],
    breadcrumbLabel: "Sleep & recovery",
  },

  "inositol-supplements": {
    slug: "inositol-supplements",
    seoTitle: "Inositol Supplements. Myo & D-Chiro Inositol | TruWe",
    seoDescription:
      "Inositol supplements at the 40:1 Myo:D-Chiro ratio studied for PCOS and cycle support. Learn what to look for on a label and how to take it.",
    h1: "Inositol supplements at the ratio the research is actually built on",
    eyebrow: "Inositol collection",
    intro:
      "Inositol is a nutrient your body already makes and uses. The ratio between its two active forms myo-inositol and D-chiro-inositol is the part most consumer supplements get wrong.",
    overview:
      "The best-studied inositol protocols for cycle regularity and insulin support use a 40:1 ratio of myo-inositol to D-chiro-inositol, roughly mirroring the ratio in plasma. Cheaper products either drop the D-chiro form entirely or use a ratio that has not been studied. TruWe's Ova Balance is formulated to this 40:1 ratio and paired with Quatrefolic® L-5-MTHF and chromium.",
    selectionGuide: {
      heading: "How to compare inositol products",
      intro:
        "Two labels can list the same milligrams and still be very different. These are the details that matter.",
      items: [
        { title: "The 40:1 ratio", body: "Look for 2000 mg myo-inositol and 50 mg D-chiro-inositol per daily serving." },
        { title: "Active folate, not folic acid", body: "Quatrefolic® L-5-MTHF is the metabolically active form of folate, the one many women can use directly." },
        { title: "Sachet vs capsule", body: "Sachets carry a larger daily dose and dissolve in water, which is why clinical protocols usually use them." },
        { title: "What to skip", body: "Products that only list D-chiro-inositol, or that hide the ratio behind a proprietary blend." },
      ],
    },
    products: [PRODUCT_LIB["ova-balance"]],
    ingredients: [
      { anchor: "myo-inositol", label: "Myo-Inositol", note: "The primary inositol form in the 40:1 ratio." },
      { anchor: "d-chiro-inositol", label: "D-Chiro-Inositol", note: "The paired form, the ratio is the story." },
      { anchor: "quatrefolic", label: "Quatrefolic® L-5-MTHF", note: "Metabolically active folate at 400 mcg." },
    ],
    safety: [
      "If you are pregnant, planning pregnancy or breastfeeding, discuss inositol with your obstetrician.",
      "If you take metformin, insulin or thyroid medication, involve your clinician before starting inositol.",
      "Discontinue and speak to a doctor if you notice unusual side effects.",
    ],
    articles: [
      { slug: "inositol-pcos", title: "Inositol for PCOS: The 40:1 Myo:D-Chiro Ratio Explained", excerpt: "Why the 40:1 ratio matters and what research shows for cycle regularity." },
      { slug: "berberine-guide", title: "Berberine: Benefits, Dosage & Evidence", excerpt: "How berberine complements inositol on the insulin-sensitivity side." },
    ],
    faqs: [
      { q: "Why the 40:1 ratio?", a: "It roughly reflects the natural ratio of myo- to D-chiro-inositol in plasma and is the ratio used in most well-designed PCOS trials." },
      { q: "How long before I might notice a change?", a: "Published protocols typically evaluate outcomes over 3-6 months of consistent use." },
      { q: "Can I combine inositol with metformin?", a: "Do not adjust prescription medication on your own. Ask your clinician; some patients are advised to take them together." },
    ],
    breadcrumbLabel: "Inositol supplements",
  },

  "pcos-nutrition": {
    slug: "pcos-nutrition",
    seoTitle: "PCOS Supplements & Inositol Nutrition Support India | TruWe",
    seoDescription:
      "Explore TruWe's PCOS nutrition range with 40:1 Myo + D-Chiro Inositol, magnesium and complementary support for cycle, metabolic and overall wellness.",
    h1: "PCOS nutrition support",
    eyebrow: "PCOS nutrition",
    intro:
      "PCOS is more than a conversation about cycles. Nutrition, metabolic health, micronutrients and everyday lifestyle all matter.",
    overview:
      "Nutrition cannot replace medical care for PCOS, but a small set of nutrients turns up in the research repeatedly: myo-inositol and D-chiro-inositol at 40:1, active folate, chromium, magnesium and zinc. TruWe pairs these with berberine HCl, a well-studied AMPK activator, in a single once-daily sachet.",
    selectionGuide: {
      heading: "Where nutrition fits into a PCOS plan",
      intro:
        "Think of nutrition as one lever alongside sleep, movement, stress and, where indicated, medical treatment.",
      items: [
        { title: "Start with inositol at 40:1", body: "The most researched nutritional intervention for PCOS in the last decade." },
        { title: "Cover the mineral gaps", body: "Magnesium, zinc and chromium support insulin signalling and stress resilience." },
        { title: "Use active folate", body: "L-5-MTHF (Quatrefolic®) is the active form the body uses directly." },
        { title: "Track, don't chase", body: "Give a protocol 3 months of consistent use and evaluate cycle length, energy and skin, one variable at a time." },
      ],
    },
    products: [PRODUCT_LIB["ova-balance"]],
    bundleSlugs: ["balance-calm-duo", "balance-detox-duo", "full-ova-balance-protocol"],
    ingredients: [
      { anchor: "myo-inositol", label: "Myo-Inositol", note: "Primary inositol in the 40:1 ratio." },
      { anchor: "berberine", label: "Berberine HCl", note: "AMPK activator studied alongside insulin-sensitivity outcomes." },
      { anchor: "quatrefolic", label: "Quatrefolic® L-5-MTHF", note: "Active folate, 400 mcg per serving." },
    ],
    safety: [
      "PCOS should be diagnosed and monitored by a qualified clinician, supplements are supportive, not a diagnosis.",
      "If you are pregnant, planning pregnancy or breastfeeding, consult your obstetrician before adding supplements.",
      "Do not stop or change prescribed medication (metformin, oral contraceptives, thyroid, etc.) based on a supplement plan.",
    ],
    articles: [
      { slug: "inositol-pcos", title: "Inositol for PCOS: The 40:1 Myo:D-Chiro Ratio Explained", excerpt: "The rationale behind the 40:1 ratio and the trials that shaped it." },
      { slug: "berberine-guide", title: "Berberine: Benefits, Dosage & Evidence", excerpt: "How berberine sits alongside inositol in a PCOS-supportive protocol." },
    ],
    faqs: [
      { q: "Can supplements alone reverse PCOS?", a: "No supplement can reverse PCOS. Nutrition is a supportive lever alongside sleep, movement and, where needed, medical care." },
      { q: "Do I still need medical follow-up?", a: "Yes. PCOS is a clinical diagnosis; ongoing monitoring is important, especially if fertility is a goal." },
      { q: "Is this safe to take long-term?", a: "The nutrients in the protocol are used long-term in trials, but any long-term plan should be reviewed with your clinician." },
    ],
    breadcrumbLabel: "PCOS nutrition",
  },

  "liver-gut-support": {
    slug: "liver-gut-support",
    seoTitle: "Liver & Gut Support Supplements. NAC, Silymarin | TruWe",
    seoDescription:
      "Liver and gut supplements built around the gut-liver axis. NAC, silymarin, curcumin, ALA and multi-strain probiotics. Learn what each ingredient supports.",
    h1: "Liver and gut support, formulated around the gut-liver axis",
    eyebrow: "Liver & gut",
    intro:
      "Your liver processes roughly seventy percent of its blood supply from the gut. That is why liver support and gut support belong in the same conversation, and often the same protocol.",
    overview:
      "Liver Biome Plus pairs NAC, liposomal silymarin, liposomal curcumin and alpha-lipoic acid with a 10 billion CFU multi-strain probiotic. The liposomal carriers exist because curcumin and silymarin are famously hard to absorb in their standard forms. The probiotic side of the formula acknowledges that day-to-day gut health is upstream of the liver's daily workload.",
    selectionGuide: {
      heading: "What to look for in a liver + gut formula",
      intro:
        "Detox teas and one-week cleanses do not do what their marketing suggests. Look for daily-use formulas with well-studied actives.",
      items: [
        { title: "Delivery form matters", body: "Standard curcumin is very poorly absorbed. Liposomal delivery is one of several evidence-backed answers." },
        { title: "NAC has decades of use", body: "NAC is a well-studied precursor to glutathione, one of the body's primary antioxidants." },
        { title: "Support both sides of the axis", body: "A multi-strain probiotic addresses the gut side that a pure liver product ignores." },
        { title: "Ignore anything called a 'flush'", body: "Liver flushes are not supported by evidence and can be counter-productive." },
      ],
    },
    products: [PRODUCT_LIB["liver-biome-plus"]],
    ingredients: [
      { anchor: "nac", label: "NAC (N-Acetyl Cysteine)", note: "Precursor to glutathione, an important cellular antioxidant." },
      { anchor: "silymarin", label: "Liposomal Silymarin", note: "Milk-thistle-derived actives, delivered in a liposomal carrier." },
      { anchor: "curcumin", label: "Liposomal Curcumin", note: "Turmeric-derived actives, delivered to address poor standard bioavailability." },
    ],
    safety: [
      "If you take blood thinners, prescription liver medication or chemotherapy, consult your doctor before starting.",
      "Do not use during pregnancy or breastfeeding without medical advice.",
      "Discontinue and see a clinician if you notice signs of allergy or unusual symptoms.",
    ],
    articles: [
      { slug: "liver-detox-truth", title: "Liver Detox: Myths vs Evidence What Actually Works", excerpt: "What the liver actually does and what the cleanse industry gets wrong." },
      { slug: "gut-liver-axis", title: "The Gut-Liver Axis: How Your Gut Controls Your Liver Health", excerpt: "Why liver support without gut support usually misses the point." },
      { slug: "curcumin-bioavailability", title: "Curcumin Bioavailability: Liposomal vs Piperine vs Standard", excerpt: "Comparing the main strategies to get curcumin actually absorbed." },
    ],
    faqs: [
      { q: "Do I need a liver cleanse?", a: "No. Your liver is not a filter that needs periodic flushing. Consistent, daily support of well-studied nutrients is the sensible approach." },
      { q: "Why liposomal delivery?", a: "Standard curcumin and silymarin have limited oral absorption. Liposomal carriers are one of the better-studied ways to address this." },
      { q: "Can I take this with medication?", a: "If you take prescription medication for the liver, blood clotting or cancer therapy, check with your doctor before starting." },
    ],
    breadcrumbLabel: "Liver & gut support",
  },

  "kids-nutrition": {
    slug: "kids-nutrition",
    seoTitle: "Kids Nutrition. No-Added-Sugar Gummies | TruWe Kids",
    seoDescription:
      "TruWe Kids nutrition, no-added-sugar gummies formulated for growing bodies. See the range, ingredients and safety notes for parents.",
    h1: "Kids nutrition, without the added sugar",
    eyebrow: "Kids nutrition",
    intro:
      "Children need nutrients, not sugar. TruWe Kids is a separate range formulated for growing bodies, no added sugar, no artificial colours, no adult-strength actives.",
    overview:
      "Most children's gummies on Indian shelves are more confectionery than nutrition. TruWe Kids is a dedicated range built around age-appropriate doses, no added sugar, no artificial colours and no flavours that mimic sweets. Adult formulations from the TruWe range are not suitable for children.",
    selectionGuide: {
      heading: "How to think about supplements for children",
      intro:
        "Food first, always. Supplements are a supportive layer, never a replacement for meals or medical care.",
      items: [
        { title: "Age-appropriate doses only", body: "Adult supplements are not paediatric, dose, form and delivery are different." },
        { title: "Read the sugar label", body: "'Healthy' gummies with 3-4 g of added sugar per serving add up quickly across a week." },
        { title: "Prefer no artificial colour", body: "Bright colours are a marketing choice, not a nutritional one." },
        { title: "Consult your paediatrician", body: "Especially if your child has allergies, is on medication or has a medical condition." },
      ],
    },
    products: [],
    external: {
      to: "/kids",
      title: "TruWe Kids",
      tagline: "No-added-sugar gummies designed for growing bodies",
      bullets: [
        "Age-appropriate formulations",
        "No added sugar, no artificial colours",
        "Made in an FSSAI-licensed GMP facility",
      ],
    },
    ingredients: [],
    safety: [
      "Always speak to your paediatrician before starting a new supplement for a child.",
      "Keep supplements out of reach of children, even sugar-free gummies can be over-consumed.",
      "Do not give adult supplements to children; dose and formulation are not comparable.",
    ],
    articles: [],
    faqs: [
      { q: "Are TruWe adult products safe for children?", a: "No. Adult supplements are formulated for adult physiology. Use the dedicated TruWe Kids range and consult your paediatrician." },
      { q: "Do you use added sugar?", a: "The TruWe Kids range is formulated without added sugar and without artificial colours." },
      { q: "At what age can my child start?", a: "This varies by child and by product. Please review the label and confirm with your paediatrician." },
    ],
    breadcrumbLabel: "Kids nutrition",
  },
};

export const COLLECTION_SLUGS = Object.keys(COLLECTIONS) as CollectionPage["slug"][];

export const COLLECTION_NAV: { slug: CollectionPage["slug"]; label: string }[] = [
  { slug: "magnesium-supplements", label: "Magnesium supplements" },
  { slug: "sleep-recovery", label: "Sleep & recovery" },
  { slug: "inositol-supplements", label: "Inositol supplements" },
  { slug: "pcos-nutrition", label: "PCOS nutrition" },
  { slug: "liver-gut-support", label: "Liver & gut support" },
  { slug: "kids-nutrition", label: "Kids nutrition" },
];

export function getCollection(slug: string): CollectionPage | undefined {
  return (COLLECTIONS as Record<string, CollectionPage>)[slug];
}

// Product handles → the most relevant collection slugs for PDP linking.
export const COLLECTIONS_FOR_PRODUCT: Record<string, CollectionPage["slug"][]> = {
  "mag-absorb-pro": ["magnesium-supplements", "sleep-recovery"],
  "liver-biome-plus": ["liver-gut-support"],
  "ova-balance": ["inositol-supplements", "pcos-nutrition"],
};
